/**
 * UserFormModal - Модальное окно для создания/редактирования пользователя
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  PasswordInput,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ROLE_INFO } from '../../../constants/permissions';

const UserFormModal = ({
  opened,
  onClose,
  onSubmit,
  user = null, // null = создание, object = редактирование
  loading = false,
}) => {
  const isEditing = !!user;

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'viewer',
    is_active: true,
  });
  const [error, setError] = useState(null);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (user) {
        setFormData({
          email: user.email || '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          password: '',
          role: user.role || 'viewer',
          is_active: user.is_active !== undefined ? user.is_active : true,
        });
      } else {
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          password: '',
          role: 'viewer',
          is_active: true,
        });
      }
      setError(null);
    }
  }, [opened, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.email) {
      setError('Email обязателен');
      return;
    }
    if (!formData.last_name) {
      setError('Фамилия обязательна');
      return;
    }
    if (!formData.first_name) {
      setError('Имя обязательно');
      return;
    }
    if (!isEditing && !formData.password) {
      setError('Пароль обязателен');
      return;
    }
    if (!isEditing && formData.password.length < 8) {
      setError('Пароль должен быть минимум 8 символов');
      return;
    }

    try {
      const data = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
      };

      if (!isEditing) {
        data.password = formData.password;
      } else {
        data.is_active = formData.is_active;
      }

      await onSubmit(data);
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  // Опции для выбора роли (без PARENT — родители создаются через отдельную страницу)
  const roleOptions = Object.entries(ROLE_INFO)
    .filter(([code]) => code !== 'PARENT')
    .map(([code, info]) => ({
      value: code,
      label: info.name,
      description: info.description,
    }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать пользователя' : 'Создать пользователя'}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          <TextInput
            label="Email"
            placeholder="user@example.com"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />

          <Group grow>
            <TextInput
              label="Фамилия"
              placeholder="Иванов"
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
            <TextInput
              label="Имя"
              placeholder="Иван"
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
          </Group>

          {!isEditing && (
            <PasswordInput
              label="Пароль"
              placeholder="Минимум 8 символов"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              description="Пользователь должен будет сменить пароль при первом входе"
            />
          )}

          <Select
            label="Роль"
            required
            value={formData.role}
            onChange={(value) => setFormData({ ...formData, role: value })}
            data={roleOptions}
            renderOption={({ option }) => (
              <div>
                <Text size="sm" fw={500}>{option.label}</Text>
                <Text size="xs" c="dimmed">{option.description}</Text>
              </div>
            )}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default UserFormModal;
